import { Actions } from "../../constants/actions";
import { Roles } from "../../constants/roles";
import { FormBase } from "../../util/form-base-form";
import type { ChoicesInstance } from "./choices-control-utils";

class ChoicesControlFormBase extends FormBase {
    public override queryElement<T extends Element = HTMLElement>(
        root: ParentNode | null | undefined,
        selector: string,
        elementClass?: abstract new (...args: never[]) => T,
    ): T | null {
        return super.queryElement(root, selector, elementClass);
    }

    public override queryElements<T extends Element = HTMLElement>(
        root: ParentNode | null | undefined,
        selector: string,
        elementClass?: abstract new (...args: never[]) => T,
    ): T[] {
        return super.queryElements(root, selector, elementClass);
    }

    public override getInput(
        root: ParentNode | Event | EventTarget | Element | null | undefined,
        selector?: string,
    ): HTMLInputElement | null {
        return super.getInput(root, selector);
    }

    public getClosestElement<T extends Element = HTMLElement>(
        root: Event | EventTarget | Element | null | undefined,
        selector: string,
        elementClass?: abstract new (...args: never[]) => T,
    ): T | null {
        return this.closest(root, selector, elementClass);
    }
}

const formBase = new ChoicesControlFormBase();

export class ChoicesControlForm {
    public static DROPDOWN_SELECTOR = ".choices__list--dropdown";

    public static INNER_SELECTOR = ".choices__inner";

    public static CLONED_INPUT_SELECTOR = "input.choices__input--cloned";

    public static getSelects(root: ParentNode): HTMLSelectElement[] {
        return formBase.queryElements(root, "select[data-choices-control]", HTMLSelectElement);
    }

    public static getBindHiddenInput(root: ParentNode, bindTo: string): HTMLInputElement | null {
        return formBase.getInput(root, `input[type="hidden"][name="${bindTo}"]`);
    }

    public static getChoicesRoot(select: HTMLSelectElement, instance: ChoicesInstance | null): HTMLElement | null {
        const fromInstance = instance?.containerOuter?.element;
        if (fromInstance instanceof HTMLElement) return fromInstance;

        const byDom = formBase.getClosestElement(select, ".choices", HTMLElement);
        return byDom instanceof HTMLElement ? byDom : null;
    }

    public static getInner(select: HTMLSelectElement, instance: ChoicesInstance | null): HTMLElement | null {
        const choicesRoot = ChoicesControlForm.getChoicesRoot(select, instance);
        const inner = formBase.queryElement(choicesRoot, ChoicesControlForm.INNER_SELECTOR, HTMLElement);
        return inner instanceof HTMLElement ? inner : null;
    }

    public static getClonedInputFromRoot(root: HTMLElement | null): HTMLInputElement | null {
        if (!(root instanceof HTMLElement)) return null;
        return formBase.getInput(root, ChoicesControlForm.CLONED_INPUT_SELECTOR);
    }

    public static getClonedInput(select: HTMLSelectElement, instance: ChoicesInstance | null): HTMLInputElement | null {
        const choicesRoot = ChoicesControlForm.getChoicesRoot(select, instance);
        return ChoicesControlForm.getClonedInputFromRoot(choicesRoot);
    }

    public static getDropdown(select: HTMLSelectElement, instance: ChoicesInstance | null): HTMLElement | null {
        const choicesRoot = ChoicesControlForm.getChoicesRoot(select, instance);
        return formBase.queryElement(choicesRoot, ChoicesControlForm.DROPDOWN_SELECTOR, HTMLElement);
    }

    public static getControlRoot(select: HTMLSelectElement): HTMLElement | null {
        const root = formBase.getClosestElement(select, `[data-role="${Roles.CHOICES_CONTROL}"]`, HTMLElement);
        return root instanceof HTMLElement ? root : null;
    }

    public static getBindWrapper(select: HTMLSelectElement): HTMLElement | null {
        return formBase.getClosestElement(select, "[data-choices-bind-to]", HTMLElement);
    }

    public static getClearButton(select: HTMLSelectElement): HTMLButtonElement | null {
        const clearRoot = ChoicesControlForm.getControlRoot(select) || select.parentElement;
        if (!(clearRoot instanceof HTMLElement)) return null;
        return formBase.queryElement(clearRoot, ChoicesControlForm.CLEAR_BUTTON_SELECTOR, HTMLButtonElement);
    }

    public static getClosestSearchMenuBody(select: HTMLSelectElement): HTMLElement | null {
        return formBase.getClosestElement(select, ".search-menu-content-choices__body", HTMLElement);
    }

    public static isChoicesButton(target: EventTarget | Element | null | undefined): boolean {
        return formBase.getClosestElement(target, ".choices__button", HTMLElement) instanceof HTMLElement;
    }

    public static isClearActionButton(target: EventTarget | Element | null | undefined): boolean {
        return (
            formBase.getClosestElement(target, ChoicesControlForm.CLEAR_BUTTON_SELECTOR, HTMLButtonElement) instanceof
            HTMLButtonElement
        );
    }

    public static isInsidePostProductPickerContainer(select: HTMLSelectElement): boolean {
        return (
            formBase.getClosestElement(
                select,
                `[data-role="${Roles.SEARCH_MENU_POST_PRODUCT_PICKER_CONTAINER}"]`,
                HTMLElement,
            ) instanceof HTMLElement
        );
    }

    public static getAllSelects(root: ParentNode = document): HTMLSelectElement[] {
        return formBase.queryElements(root, "select[data-choices-control]", HTMLSelectElement);
    }

    public static CLEAR_BUTTON_SELECTOR = `button[data-action="${Actions.CHOICES_CLEAR_ALL}"]`;

    public static getSelectedValues(select: HTMLSelectElement): string[] {
        return Array.from(select.selectedOptions)
            .map((option) => String(option.value))
            .map((value) => value.trim())
            .filter(Boolean);
    }
}
